import type { Locale } from '@t4s/api-client/model'
import { timeZoneSchema } from '@t4s/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { ApiFailure, fieldErrorsOf } from '../lib/api-error.ts'
import { messageFor } from '../lib/messages.ts'
import { NotFoundScreen } from '../screens/not-found.tsx'
import { useDocumentTitle } from '../shell/document-title.ts'
import { Notice } from '../shell/notice.tsx'
import { LocaleFields, TimeZoneField } from './configuration-fields.tsx'
import { CopyButton } from './copy-button.tsx'
import { fetchTenant, saveTenantSettings, TENANTS_QUERY_KEY, tenantQueryKey } from './gateway.ts'
import { LOCALE_LABELS, LOCALES } from './locales.ts'
import { TenantHeader } from './tenant-header.tsx'
import { canonicalTimeZone } from './time-zone.ts'
import { TimeZoneDialog } from './timezone-dialog.tsx'

/**
 * Settings — docs/ux/PLATFORM-ADMIN.md §12: the three things about a tenant that change
 * after it exists.
 *
 * **The slug is not one of them** (§9.4). It is shown as text with this estate's hostname
 * and is not editable, here or anywhere: renaming would repoint a hostname in every estate,
 * orphan their certificates, invalidate a deployment name and leave every installed mobile
 * build pointing at an address that no longer serves it, while the old slug stayed
 * permanently reserved anyway.
 *
 * **Changing the timezone is confirmed** (§19), and the confirmation says the thing an owner
 * needs to hear: day boundaries move — the calendar, exports and the nightly expiry sweep —
 * while past records keep the instants they were recorded at, so only the *day* they fall
 * into can change. That is true because of an absence rather than a promise: this write
 * changes one column, and `packages/domain`'s day-boundary functions read it at the moment
 * they are asked. Nothing recomputes and nothing is rewritten, so no day can be
 * double-expired or skipped.
 *
 * **Removing a language is not confirmed and is not destructive**: somebody whose chosen
 * language is no longer enabled falls back to the tenant's default on their next launch, and
 * the form says so in one line beside the checkboxes rather than in a dialog.
 */

/**
 * The fields this form's own `validation_failed` can name — `chore-platform-field-error-
 * mapping`. `PATCH /platform/tenants/{tenantId}`'s request body (`UpdateTenantRequestSchema`)
 * is flat, unlike `POST /platform/tenants`'s nested `superadmin` object, so every path here
 * is a bare field name.
 */
type Field = 'name' | 'timeZone' | 'defaultLocale' | 'enabledLocales'

const FIELD_FOR_PATH: Readonly<Record<string, Field>> = {
  name: 'name',
  timeZone: 'timeZone',
  defaultLocale: 'defaultLocale',
  enabledLocales: 'enabledLocales',
}

/**
 * One line per field, never the server's own prose (§20.1). `name`'s blank-after-trim and
 * the locale pair's "must contain the default" cross-check are shadowed by this form's own
 * client-side rules (the Save button disables on a blank name; the checkboxes cannot take
 * the pair out of sync) — genuinely reachable here is `name`'s 200-character cap (no
 * `maxLength` on the field) and `timeZone`'s IANA-format check (a free-text field with only
 * suggestions, same as the create form's).
 */
function copyForField(field: Field): string {
  switch (field) {
    case 'name':
      return 'Enter a company name — at most 200 characters.'
    case 'timeZone':
      return 'Enter a valid IANA time zone, such as Europe/Berlin.'
    case 'defaultLocale':
      return 'Choose a default language that is one of the enabled ones.'
    case 'enabledLocales':
      return 'Enable at least one language, including the default.'
  }
}

/**
 * **One form per tenant id** — `fix-console-settings-form-keeps-other-tenant-values`. The router
 * renders one instance of this screen for every tenant id, so a move straight from tenant A's
 * Settings page to tenant B's (the browser's history menu does it) changes only the `tenantId`
 * prop. The form's state used to survive that, and Save sent A's name, zone and languages to B.
 * The key discards the whole form — fields, edited flags, notices, the confirmation and the save
 * mutation — and starts it again as a fresh visit, exactly as `BrandingScreen` does since
 * `fix-P04b-branding-form-keeps-other-tenant-values`. See `docs/DECISIONS.md`, "Console screens
 * that act on one tenant are keyed by tenant id".
 */
export function TenantSettingsScreen({ tenantId }: { readonly tenantId: string }) {
  return <TenantSettingsForm key={tenantId} tenantId={tenantId} />
}

/** The field values one save sent, captured when it started. `undefined`: not sent. */
type Sent = {
  readonly name?: string
  readonly timeZone?: string
  readonly locales?: { readonly defaultLocale: Locale; readonly enabledLocales: readonly Locale[] }
}

function sameLocales(
  defaultLocale: Locale,
  enabledLocales: readonly Locale[],
  sent: Sent['locales'],
): boolean {
  return (
    sent !== undefined &&
    sent.defaultLocale === defaultLocale &&
    sent.enabledLocales.length === enabledLocales.length &&
    sent.enabledLocales.every((locale, index) => enabledLocales[index] === locale)
  )
}

/** The same languages, in any order — the stored tenant and the checkboxes order them differently. */
function sameSet(a: readonly Locale[], b: readonly Locale[]): boolean {
  return a.length === b.length && a.every((locale) => b.includes(locale))
}

function TenantSettingsForm({ tenantId }: { readonly tenantId: string }) {
  const queryClient = useQueryClient()
  const nameId = useId()
  const nameErrorId = useId()
  const slugHeadingId = useId()
  const formHeadingId = useId()

  const tenant = useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: () => fetchTenant(tenantId),
  })
  useDocumentTitle('Settings', tenant.data?.name, 'Tenants')

  const [name, setName] = useState('')
  const [timeZone, setTimeZone] = useState('')
  const [defaultLocale, setDefaultLocale] = useState<Locale>('de')
  const [enabledLocales, setEnabledLocales] = useState<Locale[]>(['de'])
  const [loaded, setLoaded] = useState(false)
  const [failureCode, setFailureCode] = useState<string | undefined>(undefined)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Field, string>>>({})
  const [saved, setSaved] = useState(false)
  const [confirmingZone, setConfirmingZone] = useState(false)
  // A failed save that came from the timezone confirmation is shown inside it (§19), so the owner
  // who just pressed the red button reads what happened where they pressed it.
  const [zoneError, setZoneError] = useState<string | undefined>(undefined)
  const zoneInFlight = useRef(false)
  const zoneInput = useRef<HTMLInputElement>(null)
  // Whether the operator has edited each field on this visit. Only an edited field is sent, and
  // only an unedited one follows a newer stored tenant (below). The two languages controls are one
  // edit: the server checks the default against the enabled set, so the pair is sent, and
  // followed, together.
  const [nameTouched, setNameTouched] = useState(false)
  const [zoneTouched, setZoneTouched] = useState(false)
  const [localesTouched, setLocalesTouched] = useState(false)
  // What each save sent, captured when it starts (#258's review B1, applied here). The fields stay
  // usable while a save is in flight (§12.1), so by the time the response lands the operator may
  // have typed more. `onSuccess` clears a field's edited flag only while the field still holds
  // exactly what was sent; otherwise it stays edited, keeps its text, and the next Save sends it.
  const sentRef = useRef<Sent>({})

  // The form renders disabled with its labels and fills in when the read lands (§12.1) — never
  // an empty form that fills in underneath somebody who has started typing.
  //
  // **Each unedited field follows every tenant document that lands, not only the first** —
  // `fix-console-settings-form-keeps-other-tenant-values`, the same rule
  // `fix-P04b-branding-stale-after-external-change` gave Branding. Reopening this page mounts a
  // fresh form that is handed the *cached* tenant at once while a refetch runs; filling once, from
  // the cached one, showed values changed elsewhere as they used to be, and Save wrote them back.
  // Now the refetch, or a save's own response, replaces every field not edited on this visit. An
  // edited field keeps what was typed.
  useEffect(() => {
    if (tenant.data === undefined) {
      return
    }
    if (!nameTouched) {
      setName(tenant.data.name)
    }
    if (!zoneTouched) {
      setTimeZone(tenant.data.timeZone)
    }
    if (!localesTouched) {
      setDefaultLocale(tenant.data.defaultLocale)
      setEnabledLocales([...tenant.data.enabledLocales])
    }
    setLoaded(true)
  }, [tenant.data, nameTouched, zoneTouched, localesTouched])

  // When the timezone confirmation hands a validation error back, the dialog gives way and the
  // remedy is the Timezone field — so focus goes there, where the error sentence is, rather than
  // back to Save (§12.1, §21; P06 per-task QA). The shell leaves focus alone when the page has
  // already placed it (`ModalDialog`). A Save pressed directly keeps focus on that button.
  const zoneErrorShown = fieldErrors.timeZone !== undefined
  useEffect(() => {
    if (zoneErrorShown) {
      zoneInput.current?.focus()
    }
  }, [zoneErrorShown])

  const save = useMutation({
    // A read of this tenant already in flight (the refetch on reopening the page) could answer
    // after this save's response and put the older document back into the cache, reverting the
    // unedited fields with "Saved." on screen. It is cancelled first (#258's review S1).
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: tenantQueryKey(tenantId) })
    },
    // Each field is sent only when the operator edited it on this visit — `UpdateTenantRequest`
    // leaves an absent field alone — so a Save with nothing edited sends `{}`, and a value this
    // form merely showed is never written back over a newer one.
    mutationFn: () => {
      const sent = sentRef.current
      return saveTenantSettings(tenantId, {
        ...(sent.name === undefined ? {} : { name: sent.name.trim() }),
        ...(sent.timeZone === undefined ? {} : { timeZone: sent.timeZone }),
        ...(sent.locales === undefined
          ? {}
          : {
              defaultLocale: sent.locales.defaultLocale,
              enabledLocales: [...sent.locales.enabledLocales],
            }),
      })
    },
    // This callback reads the field values of the *latest* render, not of the render Save was
    // pressed in: TanStack Query v5's `MutationObserver.setOptions` pushes each render's options
    // into the pending mutation (#258's review N1). Moving it into `mutate(…, { onSuccess })`
    // would silently break the comparison below.
    onSuccess: (updated) => {
      const sent = sentRef.current
      setNameTouched(nameTouched && name !== sent.name)
      setZoneTouched(zoneTouched && timeZone !== sent.timeZone)
      setLocalesTouched(localesTouched && !sameLocales(defaultLocale, enabledLocales, sent.locales))
      setSaved(true)
      setFailureCode(undefined)
      setFieldErrors({})
      setConfirmingZone(false)
      setZoneError(undefined)
      queryClient.setQueryData(tenantQueryKey(tenantId), updated)
      void queryClient.invalidateQueries({ queryKey: TENANTS_QUERY_KEY })
    },
    onSettled: () => {
      zoneInFlight.current = false
    },
    onError: (error) => {
      setSaved(false)
      // `onMutate` may have cancelled a read carrying a newer stored tenant; nothing else would
      // replace it, so it is read again (#258's review round 2, S1). Edited fields are untouched
      // by that, and nothing typed is lost: the state above is untouched by a failure (§12.1).
      void queryClient.invalidateQueries({ queryKey: tenantQueryKey(tenantId) })
      const failure = error instanceof ApiFailure ? error : undefined

      // `chore-platform-field-error-mapping`: a `validation_failed` naming a field this
      // form recognises marks that field and shows no generic notice, matching
      // `create-tenant.tsx`'s identical rule.
      if (failure?.code === 'validation_failed') {
        const mapped = fieldErrorsOf<Field>(failure, FIELD_FOR_PATH, copyForField)
        if (Object.keys(mapped).length > 0) {
          // The remedy is in a field behind the dialog, so the dialog gives way to it.
          setConfirmingZone(false)
          setFieldErrors(mapped)
          setFailureCode(undefined)
          return
        }
      }

      setFieldErrors({})
      const code = failure?.code ?? 'client_unknown'
      if (confirmingZone) {
        // Stays open, with the sentence inside it and the button usable again (§19).
        setZoneError(messageFor('settings', code))
        setFailureCode(undefined)
        return
      }
      setFailureCode(code)
    },
  })

  /**
   * Captures what this save sends — only edited fields — and starts it. `zone` is the timezone to
   * send when `submit` has just corrected its case and the state has not re-rendered with it yet.
   */
  const startSave = (zone: string = timeZone) => {
    sentRef.current = {
      ...(nameTouched ? { name } : {}),
      ...(zoneTouched ? { timeZone: zone } : {}),
      ...(localesTouched
        ? { locales: { defaultLocale, enabledLocales: [...enabledLocales] } }
        : {}),
    }
    save.mutate()
  }

  const retired = tenant.data?.state === 'retired'
  const zoneChanged = zoneTouched && tenant.data !== undefined && timeZone !== tenant.data.timeZone
  // §8.2, §12: a zone the owner typed is validated as an IANA name *before* anything else happens,
  // exactly as the create form does — a sentence under the field and an inert Save — so the
  // "Move business day" confirmation is never opened for a zone the server would refuse. Only an
  // edited field is checked: a stored zone is the server's own.
  const timeZoneError =
    zoneTouched && !timeZoneSchema.safeParse(timeZone).success
      ? copyForField('timeZone')
      : undefined

  // How many of the three fields hold something other than the stored tenant. A field edited
  // and then put back is not a change (nothing would differ), and the default language with the
  // enabled set is one field — the same pairing Save sends them as.
  const stored = tenant.data
  const unsaved =
    stored === undefined
      ? 0
      : [
          nameTouched && name !== stored.name,
          zoneChanged,
          localesTouched &&
            (defaultLocale !== stored.defaultLocale ||
              !sameSet(enabledLocales, stored.enabledLocales)),
        ].filter(Boolean).length

  /** Puts every field back to the stored tenant and forgets the visit's edits (§12). */
  const discard = () => {
    if (stored === undefined) {
      return
    }
    setName(stored.name)
    setTimeZone(stored.timeZone)
    setDefaultLocale(stored.defaultLocale)
    setEnabledLocales([...stored.enabledLocales])
    setNameTouched(false)
    setZoneTouched(false)
    setLocalesTouched(false)
    setFieldErrors({})
    setFailureCode(undefined)
    setSaved(false)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setSaved(false)
    setFieldErrors({})
    // Save is disabled while the zone is invalid, so a browser never submits then; this keeps the
    // rule true for any other way of submitting (and for a form submitted by script).
    if (timeZoneError !== undefined) {
      return
    }
    // The case is corrected here too, not only when the field loses focus: Enter pressed in the
    // field submits without that, and the confirmation and the request must carry the same name.
    const zone = zoneTouched ? canonicalTimeZone(timeZone) : timeZone
    if (zone !== timeZone) {
      setTimeZone(zone)
    }
    if (zoneTouched && tenant.data !== undefined && zone !== tenant.data.timeZone) {
      setZoneError(undefined)
      setConfirmingZone(true)
      return
    }
    startSave(zone)
  }

  // F-2: this route's *read* — `fetchTenant`, keyed by the URL's `tenantId` — never had a
  // rendering path for its own failure, so a malformed or nonexistent id left the form
  // permanently empty and disabled with an inert Save button, neither Not found nor any
  // error notice. This mirrors the fix already on the tenant page (F-1, `tenant-page.tsx`):
  // a malformed id fails the API's id-shape check before it ever looks the tenant up and
  // comes back `validation_failed` rather than `not_found`, but §18.3 requires the two to
  // read identically, so both fold into Not found here.
  //
  // This is deliberately **separate** from `failureCode`, which is set only by the `save`
  // mutation's `onError` — a real form submission whose own `validation_failed` (an invalid
  // IANA timezone, say) must still render as field-level validation feedback, not Not
  // found. The route-parameter read below can never produce `failureCode`, and `failureCode`
  // can never suppress this branch, so a bad save on a tenant that *did* load correctly is
  // unaffected.
  // Only a read that has produced **nothing** replaces the form. A refresh of a tenant already
  // read that fails leaves what is on screen on screen (§18.1) — the same rule as the Overview.
  if (tenant.isError && tenant.data === undefined) {
    const code = tenant.error instanceof ApiFailure ? tenant.error.code : 'client_unknown'
    if (code === 'not_found' || code === 'validation_failed') {
      // The router's own not-found page, not a copy of it (`NotFoundScreen`): what a
      // stranger reads must not depend on which kind of wrong address they guessed.
      return <NotFoundScreen />
    }
    return (
      <main className="page">
        <TenantHeader tenantId={tenantId} />
        <h2 className="visually-hidden">Settings</h2>
        <Notice tone="failure">{messageFor('settings', code)}</Notice>
        <button
          type="button"
          className="button"
          onClick={() => {
            void tenant.refetch()
          }}
        >
          Try again
        </button>
      </main>
    )
  }

  return (
    <main className="page">
      <TenantHeader tenantId={tenantId} />
      <h2 className="visually-hidden">Settings</h2>

      <div className="tab-narrow">
        {saved ? (
          <Notice tone="success" tag="Saved">
            The name, timezone and languages were saved.
          </Notice>
        ) : null}
        {failureCode === undefined ? null : (
          <Notice tone="failure">{messageFor('settings', failureCode)}</Notice>
        )}

        <section className="card" aria-labelledby={slugHeadingId}>
          <div className="card-head">
            <div>
              <h2 id={slugHeadingId}>Slug</h2>
              <p className="muted">
                A slug cannot be changed. It is this tenant’s hostname in every estate it exists in,
                a deployment name and an app-store identifier, and it stays reserved even after a
                tenant is retired.
              </p>
            </div>
          </div>
          <div className="card-body">
            <div className="row">
              <code className="keybox">{tenant.data?.slug ?? '—'}</code>
              {tenant.data === undefined ? null : (
                <CopyButton value={tenant.data.slug} noun="slug" />
              )}
            </div>
          </div>
        </section>

        <form className="card" onSubmit={submit} noValidate aria-labelledby={formHeadingId}>
          <div className="card-head">
            <div>
              <h2 id={formHeadingId}>Name, timezone and languages</h2>
              <p className="muted">Only what you change is sent.</p>
            </div>
          </div>
          <div className="card-body form">
            <div className="field">
              <label htmlFor={nameId}>Company name</label>
              <input
                id={nameId}
                value={name}
                required
                disabled={!loaded || retired}
                aria-invalid={fieldErrors.name !== undefined}
                aria-describedby={nameErrorId}
                onChange={(event) => {
                  setName(event.target.value)
                  setNameTouched(true)
                }}
              />
              {fieldErrors.name === undefined ? null : (
                <p className="field-error" id={nameErrorId}>
                  {fieldErrors.name}
                </p>
              )}
            </div>

            <TimeZoneField
              value={timeZone}
              onChange={(value) => {
                setTimeZone(value)
                setZoneTouched(true)
              }}
              disabled={!loaded || retired}
              error={timeZoneError ?? fieldErrors.timeZone}
              inputRef={zoneInput}
            />

            <LocaleFields
              locales={LOCALES}
              labels={LOCALE_LABELS}
              defaultLocale={defaultLocale}
              enabledLocales={enabledLocales}
              onDefaultChange={(value) => {
                setDefaultLocale(value)
                setLocalesTouched(true)
              }}
              onEnabledChange={(value) => {
                setEnabledLocales(value)
                setLocalesTouched(true)
              }}
              disabled={!loaded || retired}
              defaultLocaleError={fieldErrors.defaultLocale}
              enabledLocalesError={fieldErrors.enabledLocales}
            />
          </div>
          <div className="card-foot">
            {retired ? (
              <span className="unsaved">
                A retired tenant is not configured. Its settings are read-only.
              </span>
            ) : (
              <>
                <span className="unsaved" role="status">
                  {unsaved === 0
                    ? 'No unsaved changes'
                    : `${unsaved} unsaved ${unsaved === 1 ? 'change' : 'changes'}`}
                </span>
                <div className="row">
                  <button
                    type="button"
                    className="button"
                    disabled={unsaved === 0 || save.isPending}
                    onClick={discard}
                  >
                    Discard
                  </button>
                  <button
                    type="submit"
                    className="button button-primary"
                    disabled={
                      !loaded || save.isPending || name.trim() === '' || timeZoneError !== undefined
                    }
                    aria-busy={save.isPending}
                  >
                    {save.isPending ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </>
            )}
          </div>
        </form>
      </div>

      {confirmingZone && tenant.data !== undefined ? (
        <TimeZoneDialog
          from={tenant.data.timeZone}
          to={timeZone}
          pending={save.isPending}
          error={zoneError}
          onCancel={() => {
            setConfirmingZone(false)
            setZoneError(undefined)
          }}
          onConfirm={() => {
            // A double press in one frame sends one request: the ref is set here and released
            // when the save settles, before the button's `pending` has had a render to disable it.
            if (zoneInFlight.current) {
              return
            }
            zoneInFlight.current = true
            setZoneError(undefined)
            startSave()
          }}
        />
      ) : null}
    </main>
  )
}
